import { api } from '..';
import {
  CatalogSyncStatusResponse,
  ChannelHealth,
  ConversionEventsConfig,
  MetaChannel,
  TriggerSyncResponse,
} from './types';
import { ZigiBusinessesResponse, ZigiRegisterWebhookBody, ZigiRegisterWebhookResponse } from './zigiTypes';

// The manual catalog sync endpoint is synchronous on the backend: a full sync of
// ~170 products takes several seconds, so it needs a generous request timeout.
const CATALOG_SYNC_TIMEOUT_MS = 120000;

export const integrationsApi = api
  .enhanceEndpoints({ addTagTypes: ['Integrations', 'CatalogSyncStatus', 'ConversionEventsConfig'] })
  .injectEndpoints({
    endpoints: build => ({
      getIntegration: build.query<any, { slug: string }>({
        query: ({ slug }) => ({
          url: `v1/integrations/${slug}`,
          method: 'GET',
        }),
        providesTags: ['Integrations'],
      }),
      updateIntegration: build.mutation<any, { slug: string; body: any }>({
        query: ({ slug, body }) => ({
          url: `v1/integrations/${slug}`,
          method: 'PUT',
          body,
        }),
        // Meta's access tokens are integration-level settings and decide whether
        // its channels operate, so the derived status endpoints refresh as well.
        invalidatesTags: ['Integrations', 'CatalogSyncStatus', 'ConversionEventsConfig'],
      }),
      toggleIntegration: build.mutation<any, { slug: string; enabled: boolean }>({
        query: ({ slug, enabled }) => ({
          url: `v1/integrations/${slug}/toggle`,
          method: 'PATCH',
          body: { enabled },
        }),
        // The master switch changes the effective state of every channel, so the
        // derived status endpoints must be invalidated too.
        invalidatesTags: ['Integrations', 'CatalogSyncStatus', 'ConversionEventsConfig'],
      }),
      // Replaces the channel settings object as a whole — never send partial settings.
      updateIntegrationChannel: build.mutation<
        any,
        { slug: string; channel: MetaChannel; enabled?: boolean; settings?: Record<string, unknown> }
      >({
        query: ({ slug, channel, enabled, settings }) => ({
          url: `v1/integrations/${slug}/channels/${channel}`,
          method: 'PUT',
          body: {
            ...(enabled !== undefined ? { enabled } : {}),
            ...(settings !== undefined ? { settings } : {}),
          },
        }),
        invalidatesTags: ['Integrations', 'CatalogSyncStatus', 'ConversionEventsConfig'],
      }),
      getCatalogSyncStatus: build.query<CatalogSyncStatusResponse, void>({
        query: () => ({
          url: 'v1/admin/catalog-sync/status',
          method: 'GET',
        }),
        providesTags: ['CatalogSyncStatus'],
      }),
      triggerCatalogSync: build.mutation<TriggerSyncResponse, { provider: string; mode: 'full' | 'delta' }>({
        query: ({ provider, mode }) => ({
          url: `v1/admin/catalog-sync/${provider}/sync`,
          method: 'POST',
          body: { mode },
          timeout: CATALOG_SYNC_TIMEOUT_MS,
        }),
        invalidatesTags: ['CatalogSyncStatus'],
      }),
      getConversionEventsConfig: build.query<ConversionEventsConfig, void>({
        query: () => ({
          url: 'v1/analytics/conversion-events/config',
          method: 'GET',
        }),
        providesTags: ['ConversionEventsConfig'],
      }),
      listZigiBusinesses: build.query<ZigiBusinessesResponse, void>({
        query: () => ({
          url: 'v1/admin-area/orders/zigi/businesses',
          method: 'GET',
        }),
      }),
      registerZigiWebhook: build.mutation<ZigiRegisterWebhookResponse, ZigiRegisterWebhookBody>({
        query: body => ({
          url: 'v1/admin-area/orders/zigi/webhooks',
          method: 'POST',
          body,
        }),
      }),
      // Admin-only counterpart of the public config: says what keeps the channel off.
      getConversionEventsStatus: build.query<ChannelHealth, void>({
        query: () => ({
          url: 'v1/analytics/conversion-events/status',
          method: 'GET',
        }),
        providesTags: ['ConversionEventsConfig'],
      }),
    }),
  });

export const {
  useLazyGetIntegrationQuery,
  useGetIntegrationQuery,
  useUpdateIntegrationMutation,
  useToggleIntegrationMutation,
  useUpdateIntegrationChannelMutation,
  useGetCatalogSyncStatusQuery,
  useTriggerCatalogSyncMutation,
  useGetConversionEventsConfigQuery,
  useLazyListZigiBusinessesQuery,
  useRegisterZigiWebhookMutation,
  useGetConversionEventsStatusQuery,
} = integrationsApi;
